import { computed } from '@nuxtjs/composition-api';
import { storeToRefs } from 'pinia'
import { FLOW_STEP_STATUS } from '~/constants/FlowStepStatus';
import { FLOW_STATUS } from '~/constants/CheckoutFlow';
import { useFlowStore } from '~/stores/useFlowStore';
import { useFlowValidationStore } from '~/stores/useFlowValidationStore';

export default function useFlow(app, state) {
  const flowStore = useFlowStore()
  const flowValidationStore = useFlowValidationStore()
  const {
    preValidateInProgress,
    validateInProgress,
    validationResponse,
    validationStatus,
    preValidationStatus,
    signupBlockingNotifications,
    hasSignupBlockingNotification,
    signupBlockingNotificationsValidate,
    hasSignupBlockingNotificationValidate
  } = storeToRefs(flowValidationStore)


  const { panels, flowHandler } = state
  let waitTime = false; // ensure it doesnt skip a step. prevent double clicks. or if script execution hangs a moment etc.

  const initializeFlow = (appContext) => {
    flowStore.reset()
    flowHandler.initialize(appContext)
    preValidate()
    flowStore.setStatus(FLOW_STATUS.IN_PROGRESS)
    panels.forEach(() => {
      flowStore.addStep({
        status: FLOW_STEP_STATUS.INCOMPLETE
      })
    })
  }

  const preValidate = () => {
    preValidateInProgress.value = true
    return flowHandler.preValidate().then((res) => {
      preValidationStatus.value = res.actionStatus
      hasSignupBlockingNotification.value = res.hasSignupBlockingNotification
      signupBlockingNotifications.value = res.signupBlockingNotifications
      preValidateInProgress.value = false
    })
  }

  const validate = (subscriptionSignUpDataNew) => {
    validateInProgress.value = true
    hasSignupBlockingNotificationValidate.value = null
    signupBlockingNotificationsValidate.value = null
    return flowHandler.validate(subscriptionSignUpDataNew).then((res) => {
      validationResponse.value = res
      validationStatus.value = res.actionStatus
      hasSignupBlockingNotificationValidate.value = res.hasSignupBlockingNotification
      signupBlockingNotificationsValidate.value = res.signupBlockingNotifications
      if (hasSignupBlockingNotificationValidate.value) {
        flowStore.setStatus(FLOW_STATUS.ERROR)
      }
      validateInProgress.value = false
    })
  }

  const nextStep = async (index) => {
    await flowHandler.finalizeStep(panels[index].stepType, state, app)
    if (waitTime) { return; }
    waitTime = true
    setTimeout(() => {
      if (flowStore.getCurrentStepStatus !== FLOW_STEP_STATUS.ERROR) {
        flowStore.setStepStatus(index, FLOW_STEP_STATUS.COMPLETE)
        flowStore.increaseCurrentStep()
        flowStore.updatePanelModel()
        $nuxt.$emit('flowStepNext', index)
      }(waitTime = false)
    }, 500)
  }

  /**
   * edit step in the checkout
   * @param {number} i
   */
  const editStep = (index) => {
    if (index !== -1) {
      flowStore.setStepStatus(index, FLOW_STEP_STATUS.EDITING)
      flowStore.setCurrentStep(index)
      flowStore.updatePanelModel()
      $nuxt.$emit('flowStepEdit', index)
      flowStore.steps.forEach((_step, i) => {
        if (i > flowStore.currentStep) {
          flowStore.setStepStatus(i, FLOW_STEP_STATUS.EDITING)
        }
      })
    }
  }

  // to stop default panel toggle behavior on click
  const valueComparator = (_a, b) => {
    return b === flowStore.getCurrentStep.value
  }

  const next = async (index) => {
    if (isStepValidationRequired(index)) {
      flowStore.setStepStatus(index, FLOW_STEP_STATUS.FORM_SUBMITTED)
    } else {
      await nextStep(index)
    }
  }

  const isStepValidationRequired = (index) => {
    return panels[index].validationComponent !== undefined
  }

  const performStepFailure = (index) => {
    flowStore.setStepStatus(index, FLOW_STEP_STATUS.ERROR)
  }

  const performStepSuccess = () => {
    flowStore.setStepStatus(flowStore.currentStep, FLOW_STEP_STATUS.FORM_SUBMITTED)
  }

  const isComplete = (index) => {
    return flowStore.getStepStatus(index) === FLOW_STEP_STATUS.COMPLETE
  }

  const currentStepType = computed(() => panels[flowStore.currentStep].stepType)
  const getCurrentStepStatus = computed(() => flowStore.getCurrentStepStatus)

  return {
    validationResponse,
    currentStepType,
    getCurrentStepStatus,
    setFlowStepStatus: flowStore.setStepStatus,
    flowStatus: flowStore.getFlowStatus,
    validateInProgress,
    signupBlockingNotificationsValidate,
    hasSignupBlockingNotificationValidate,
    preValidateInProgress,
    hasSignupBlockingNotification,
    signupBlockingNotifications,
    validationStatus,
    preValidationStatus,
    preValidate,
    validate,
    initializeFlow,
    isComplete,
    valueComparator,
    panelModel: flowStore.getPanelModel,
    currentStep: flowStore.getCurrentStep,
    steps: flowStore.getSteps,
    editStep,
    nextStep,
    waitTime,
    performStepFailure,
    performStepSuccess,
    next,
    panels
  }
}
